'use client'

import React, { ChangeEvent, CSSProperties, Profiler, useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import HeaderSelect from './HeaderSelect'
import { useTranslation } from 'react-i18next'
import { usePathname } from 'next/navigation'
import { useGetUserByToken } from '@/helpers/useGetUserByToken'
import classNames from 'classnames'
import s from './Header.module.scss'
import { HeaderLogo } from './HeaderLogo'
import { HeaderBurger } from './HeaderBurger'

interface Props {
    burgerBg?: string
    isWhiteLogo?: boolean
    className?: string
    style?: CSSProperties
    isLightTheme?: boolean
    toggleTheme?: any
}

const Header: React.FC<Props> = ({ burgerBg, toggleTheme, isWhiteLogo = false, className, style, isLightTheme }) => {
    const { t } = useTranslation()
    const user = useGetUserByToken()

    const [searchActive, setSearchActive] = useState(false)
    const [mobileActive, setMobileActive] = useState(false)
    const [screenWidth, setScreenWidth] = useState(1200)
    const headerRef = useRef<HTMLElement>(null)
    const pathname = usePathname()
    const mobileIconRef = useRef<any>(null)
    const [hasBeenCalled, setHasBeenCalled] = useState(false)
    const [isArticlePage, setArticlePage] = useState(false)

    // const isArticlePage = pathname.includes('news/') || pathname.includes('useful-materials/')
    useEffect(() => {
        setArticlePage(pathname.includes('news/') || pathname.includes('useful-materials/'))
    }, [pathname])

    const blueBg =
        burgerBg === 'linear-gradient(180deg, #3156A8 21.87%, #2D3079 108.55%)' ||
        'linear-gradient(180deg, rgba(55, 87, 166, 0) 0%, rgba(42, 58, 126, 0.3) 100%)'

    const handleMore = useCallback(() => {
        headerRef.current.classList.add('light')

        setHasBeenCalled(true)
    }, [headerRef, setHasBeenCalled])

    const handleLess = useCallback(() => {
        headerRef.current.classList.remove('light')

        setHasBeenCalled(false)
    }, [headerRef, setHasBeenCalled])

    useEffect(() => {
        if (isArticlePage) {
            const handleScroll = () => {
                const newScrollTop = window.scrollY
                if (newScrollTop >= (screenWidth > 1080 ? 630 : 508) && !hasBeenCalled) {
                    handleMore()
                }
                if (newScrollTop <= (screenWidth > 1080 ? 630 : 508) && hasBeenCalled) {
                    handleLess()
                }
            }

            window.addEventListener('scroll', handleScroll)

            return () => {
                window.removeEventListener('scroll', handleScroll)
            }
        }
    }, [hasBeenCalled])

    useEffect(() => {
        if (isArticlePage) {
            const updateScreenWidth = () => {
                setScreenWidth(window.innerWidth)
            }

            updateScreenWidth()

            window.addEventListener('resize', updateScreenWidth)

            return () => {
                window.removeEventListener('resize', updateScreenWidth)
            }
        }
    }, [screenWidth])

    return (
        <header
            ref={headerRef}
            className={classNames('header', className)}
            style={style ? style : {}}
        >
            <div className='header__menu container'>
                <HeaderLogo isWhiteLogo={isWhiteLogo} />
                <HeaderBurger
                    setMobileActive={setMobileActive}
                    burgerBg={burgerBg}
                    blueBg={blueBg}
                    searchActive={searchActive}
                    setSearchActive={setSearchActive}
                    toggleTheme={toggleTheme}
                    isLightTheme={isLightTheme}
                    mobileIconRef={mobileIconRef}
                />
                <ul className='header__menu-list'>
                    {!searchActive && (
                        <>
                            <li className='header__menu-item'>
                                <Link
                                    className={classNames(
                                        'header__menu-link',
                                        isLightTheme ? 'header__menu-lightLink' : ''
                                    )}
                                    href='/catalog'
                                >
                                    {t('catalog')}
                                </Link>
                            </li>
                            <li className='header__menu-item'>
                                <Link
                                    className={classNames(
                                        'header__menu-link',
                                        isLightTheme ? 'header__menu-lightLink' : ''
                                    )}
                                    href='/b2b'
                                >
                                    {t('aboutUs')}
                                </Link>
                            </li>
                            <li className='header__menu-item'>
                                <Link
                                    className={classNames(
                                        'header__menu-link',
                                        isLightTheme ? 'header__menu-lightLink' : ''
                                    )}
                                    href='/media'
                                >
                                    {t('news')}
                                </Link>
                            </li>
                            <li className='header__menu-item'>
                                <Link
                                    className={classNames(
                                        'header__menu-link',
                                        isLightTheme ? 'header__menu-lightLink' : ''
                                    )}
                                    href='#contact'
                                >
                                    {t('contacts')}
                                </Link>
                            </li>

                            <li
                                className='header__menu-item'
                                onClick={toggleTheme}
                            >
                                {isLightTheme ? (
                                    <svg
                                        width='18'
                                        height='18'
                                        viewBox='0 0 18 18'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            d='M16.5 10.6667C16.1877 12.0352 15.4974 13.2887 14.5079 14.2844C13.5184 15.2801 12.2693 15.9781 10.9027 16.299C9.53615 16.6199 8.10692 16.5509 6.77763 16.0997C5.44834 15.6486 4.27235 14.8334 3.38343 13.7469C2.49452 12.6605 1.92835 11.3463 1.74934 9.95404C1.57034 8.56173 1.78567 7.14713 2.37085 5.87115C2.95604 4.59517 3.88758 3.50903 5.05951 2.73628C6.23144 1.96354 7.59672 1.5352 9.00005 1.5C7.78447 2.49456 7.01377 3.93127 6.85749 5.49408C6.70121 7.05688 7.17215 8.61776 8.16671 9.83333C9.16128 11.0489 10.598 11.8196 12.1608 11.9759C13.7236 12.1322 15.2845 11.6612 16.5 10.6667Z'
                                            stroke='black'
                                            stroke-width='1.66667'
                                            stroke-linecap='round'
                                            stroke-linejoin='round'
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        width='20'
                                        height='20'
                                        viewBox='0 0 20 20'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            fill-rule='evenodd'
                                            clip-rule='evenodd'
                                            d='M10 3.75C9.75136 3.75 9.5129 3.65123 9.33709 3.47541C9.16127 3.2996 9.0625 3.06114 9.0625 2.8125V0.9375C9.0625 0.68886 9.16127 0.450403 9.33709 0.274587C9.5129 0.098772 9.75136 0 10 0C10.2486 0 10.4871 0.098772 10.6629 0.274587C10.8387 0.450403 10.9375 0.68886 10.9375 0.9375V2.8125C10.9375 3.06114 10.8387 3.2996 10.6629 3.47541C10.4871 3.65123 10.2486 3.75 10 3.75ZM10 13.125C10.8288 13.125 11.6237 12.7958 12.2097 12.2097C12.7958 11.6237 13.125 10.8288 13.125 10C13.125 9.1712 12.7958 8.37634 12.2097 7.79029C11.6237 7.20424 10.8288 6.875 10 6.875C9.1712 6.875 8.37634 7.20424 7.79029 7.79029C7.20424 8.37634 6.875 9.1712 6.875 10C6.875 10.8288 7.20424 11.6237 7.79029 12.2097C8.37634 12.7958 9.1712 13.125 10 13.125ZM10 15C11.3261 15 12.5979 14.4732 13.5355 13.5355C14.4732 12.5979 15 11.3261 15 10C15 8.67392 14.4732 7.40215 13.5355 6.46447C12.5979 5.52678 11.3261 5 10 5C8.67392 5 7.40215 5.52678 6.46447 6.46447C5.52678 7.40215 5 8.67392 5 10C5 11.3261 5.52678 12.5979 6.46447 13.5355C7.40215 14.4732 8.67392 15 10 15ZM9.0625 19.0625C9.0625 19.3111 9.16127 19.5496 9.33709 19.7254C9.5129 19.9012 9.75136 20 10 20C10.2486 20 10.4871 19.9012 10.6629 19.7254C10.8387 19.5496 10.9375 19.3111 10.9375 19.0625V17.1875C10.9375 16.9389 10.8387 16.7004 10.6629 16.5246C10.4871 16.3488 10.2486 16.25 10 16.25C9.75136 16.25 9.5129 16.3488 9.33709 16.5246C9.16127 16.7004 9.0625 16.9389 9.0625 17.1875V19.0625ZM16.25 10C16.25 9.75136 16.3488 9.5129 16.5246 9.33709C16.7004 9.16127 16.9389 9.0625 17.1875 9.0625H19.0625C19.3111 9.0625 19.5496 9.16127 19.7254 9.33709C19.9012 9.5129 20 9.75136 20 10C20 10.2486 19.9012 10.4871 19.7254 10.6629C19.5496 10.8387 19.3111 10.9375 19.0625 10.9375H17.1875C16.9389 10.9375 16.7004 10.8387 16.5246 10.6629C16.3488 10.4871 16.25 10.2486 16.25 10ZM0.9375 9.0625C0.68886 9.0625 0.450403 9.16127 0.274587 9.33709C0.098772 9.5129 0 9.75136 0 10C0 10.2486 0.098772 10.4871 0.274587 10.6629C0.450403 10.8387 0.68886 10.9375 0.9375 10.9375H2.8125C3.06114 10.9375 3.2996 10.8387 3.47541 10.6629C3.65123 10.4871 3.75 10.2486 3.75 10C3.75 9.75136 3.65123 9.5129 3.47541 9.33709C3.2996 9.16127 3.06114 9.0625 2.8125 9.0625H0.9375ZM14.42 5.58C14.2444 5.40422 14.1458 5.16594 14.1458 4.9175C14.1458 4.66906 14.2444 4.43078 14.42 4.255L15.745 2.93C15.9227 2.7644 16.1578 2.67425 16.4007 2.67853C16.6435 2.68282 16.8753 2.78121 17.047 2.95297C17.2188 3.12474 17.3172 3.35647 17.3215 3.59935C17.3258 3.84222 17.2356 4.07728 17.07 4.255L15.745 5.58C15.5692 5.75556 15.3309 5.85418 15.0825 5.85418C14.8341 5.85418 14.5958 5.75556 14.42 5.58ZM2.92875 15.745C2.76315 15.9227 2.673 16.1578 2.67728 16.4007C2.68157 16.6435 2.77996 16.8753 2.95172 17.047C3.12349 17.2188 3.35522 17.3172 3.5981 17.3215C3.84097 17.3258 4.07603 17.2356 4.25375 17.07L5.58125 15.745C5.75194 15.5681 5.84633 15.3312 5.84408 15.0854C5.84182 14.8396 5.74312 14.6045 5.56921 14.4308C5.39531 14.2571 5.16013 14.1586 4.91432 14.1566C4.6685 14.1545 4.43173 14.2491 4.255 14.42L2.92875 15.745ZM14.42 14.42C14.5958 14.2444 14.8341 14.1458 15.0825 14.1458C15.3309 14.1458 15.5692 14.2444 15.745 14.42L17.07 15.745C17.2356 15.9227 17.3258 16.1578 17.3215 16.4007C17.3172 16.6435 17.2188 16.8753 17.047 17.047C16.8753 17.2188 16.6435 17.3172 16.4007 17.3215C16.1578 17.3258 15.9227 17.2356 15.745 17.07L14.42 15.745C14.2444 15.5692 14.1458 15.3309 14.1458 15.0825C14.1458 14.8341 14.2444 14.5958 14.42 14.42ZM4.255 2.92875C4.07728 2.76315 3.84222 2.673 3.59935 2.67728C3.35647 2.68157 3.12474 2.77996 2.95297 2.95172C2.78121 3.12349 2.68282 3.35522 2.67853 3.5981C2.67425 3.84097 2.7644 4.07603 2.93 4.25375L4.255 5.58C4.34083 5.67211 4.44433 5.74599 4.55933 5.79723C4.67433 5.84847 4.79847 5.87602 4.92435 5.87824C5.05023 5.88046 5.17526 5.8573 5.292 5.81015C5.40873 5.763 5.51477 5.69282 5.6038 5.6038C5.69282 5.51477 5.763 5.40873 5.81015 5.292C5.8573 5.17526 5.88046 5.05023 5.87824 4.92435C5.87602 4.79847 5.84847 4.67433 5.79723 4.55933C5.74599 4.44433 5.67211 4.34083 5.58 4.255L4.255 2.92875Z'
                                            fill='black'
                                        />
                                    </svg>
                                )}
                            </li>
                            <HeaderSelect lightTheme={isLightTheme} />
                        </>
                    )}
                    {!searchActive && (
                        <li className='header__menu-item'>
                            <div
                                ref={mobileIconRef}
                                className={classNames(
                                    'header__menu-link header__menu-phone',
                                    isLightTheme ? s.phoneIcoLight : ''
                                )}
                                // href='#'
                            >
                                <svg
                                    className={`header__menu-mobile ${mobileActive && 'header__menu-mobile_active'}`}
                                    onClick={() => setMobileActive(!mobileActive)}
                                    width='20'
                                    height='20'
                                    viewBox='0 0 20 20'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        d='M6.57159 2H3.45434C2.65103 2 2 2.65103 2 3.45434C2 11.4874 8.51255 18 16.5457 18C17.349 18 18 17.349 18 16.5457V13.4284L14.5716 11.1432L12.7939 12.9197C12.485 13.2287 12.0237 13.3258 11.6353 13.125C10.6212 12.5974 9.6859 11.9303 8.85683 11.1432C8.01768 10.3382 7.33129 9.38786 6.8309 8.33821C6.65435 7.96303 6.75697 7.52938 7.05048 7.23476L8.85683 5.42841L6.57159 2Z'
                                        stroke='#7987AC'
                                        strokeWidth='1.6'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                                <div
                                    className={
                                        mobileActive ? 'header__menu-mobile-text_active' : 'header__menu-mobile-text'
                                    }
                                >
                                    <a
                                        href={`tel:+78008888888`}
                                        className='header__menu-item'
                                        style={{ color: '#7ec9ff' }}
                                    >
                                        +7 (8442) 97-97-97
                                    </a>
                                </div>
                            </div>
                        </li>
                    )}
                    {!searchActive ? (
                        user ? (
                            <li
                                style={{ paddingTop: '3px' }}
                                className='header__menu-item'
                            >
                                <a
                                    href={user ? '/profile' : '#'}
                                    className='header__menu-link'
                                >
                                    <svg
                                        width='20'
                                        height='20'
                                        viewBox='0 0 20 20'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            d='M3 18C5.9 12.3 14.1 12.3 17 18'
                                            stroke='#7987ac'
                                            stroke-width='1.6'
                                            stroke-linecap='round'
                                        />
                                        <path
                                            d='M10 11C12.2091 11 14 9.20914 14 7C14 4.79086 12.2091 3 10 3C7.79086 3 6 4.79086 6 7C6 9.20914 7.79086 11 10 11Z'
                                            stroke='#7987ac'
                                            stroke-width='1.6'
                                        />
                                    </svg>
                                </a>
                            </li>
                        ) : null
                    ) : null}
                </ul>
            </div>
        </header>
        // </Profiler>
    )
}

export default Header
